import InfoBlock from './InfoBlock'
import parse from 'html-react-parser'
import SingleQuestion from './SingleQuestion'
import React, { FC, useCallback, useEffect, useMemo } from 'react'

import {
  Option,
  QuestionCheckboxMulti,
  QuestionInput,
} from '@mortgage-pos/types'
import { useShouldShowQuestion } from '../utils/visibleIf'

interface Props {
  name: string
  title: string
  error: string
  value: string[]
  content: string
  bottomContent: string
  touched: boolean
  options: Option[]
  modifierClass: string
  multiAnswerEnabled: boolean
  infoContent?: string
  questionGroup?: string
  questionGroupValues?: object
  setField: (value: object) => void
}

export const CheckboxMulti: FC<Props> = ({
  name,
  title,
  value,
  content,
  bottomContent,
  error,
  touched,
  options,
  modifierClass,
  multiAnswerEnabled,
  infoContent,
  questionGroup,
  questionGroupValues,
  setField,
}) => {
  const errorClass = error && touched ? '--error' : ''

  const handleCheckboxChange = (checkboxValue) => {
    const updatedValue = toggleCheckedValue(
      value,
      checkboxValue,
      multiAnswerEnabled
    )

    const updatedFields = {
      [name]: updatedValue,
    }

    if (!updatedValue.includes(checkboxValue)) {
      const changedOption = options.find(
        (option) => option.value === checkboxValue
      )
      if (changedOption.subOptions?.length) {
        const nestedName = changedOption.subOptions[0].questionName.name
        updatedFields[`${questionGroup}.${nestedName}`] = []
      }
    }

    setField(updatedFields)
  }

  return (
    <div
      className={`checkbox-multi ${modifierClass || ''} ${errorClass}`}
      data-testid={`checkbox-multi-${name}`}
    >
      {title ? <h1 className="checkbox-multi__title">{title}</h1> : null}
      {content ? <div>{parse(content)}</div> : null}
      <ul className="checkbox-multi__options">
        {options.map((option, index) => {
          const isChecked = value?.includes(option.value)

          return (
            <Checkbox
              key={`checkbox-multi-${index}`}
              name={name}
              value={option.value}
              isChecked={isChecked}
              text={option.text}
              inputField={option.inputField?.[0]}
              subOptions={option.subOptions?.[0]}
              questionGroup={questionGroup}
              questionGroupValues={questionGroupValues}
              onChange={handleCheckboxChange}
              setField={setField}
              testId={option.value}
            />
          )
        })}
      </ul>
      {bottomContent ? <div>{parse(bottomContent)}</div> : null}
      {!infoContent || (touched && error) ? null : (
        <InfoBlock>{infoContent}</InfoBlock>
      )}
      {error && touched ? <InfoBlock type="danger">{error}</InfoBlock> : null}
    </div>
  )
}
interface CheckboxProps {
  name: string
  isChecked: boolean
  text: string
  value: string
  inputField: QuestionInput
  subOptions: QuestionCheckboxMulti
  questionGroup: string
  questionGroupValues: object
  testId?: string
  setField: (value: object) => void
  onChange: (value: string) => void
}

const Checkbox: FC<CheckboxProps> = ({
  name,
  isChecked,
  text,
  value,
  inputField,
  subOptions,
  questionGroup,
  questionGroupValues,
  testId = null,
  setField,
  onChange,
}) => {
  const checkedClass = isChecked ? '--checked' : ''

  const handleCheckboxChange = useCallback(() => {
    onChange(value)
  }, [onChange, value])

  const handleLabelClick = (event: React.MouseEvent) => {
    // Prevent checkbox toggle when clicking on links
    if (
      event.target instanceof HTMLAnchorElement ||
      (event.target as HTMLElement).closest('a')
    ) {
      event.stopPropagation()
      return
    }
    handleCheckboxChange()
  }

  const handleKeyPress = (event): void => {
    if (['Enter', ' '].includes(event.key)) {
      handleCheckboxChange()
    }
  }

  const handleInputChange = useCallback(
    (e) => {
      const inputValue = e.target.value

      const shouldUpdateCheckbox = !!(
        (isChecked && !inputValue.length) ||
        (!isChecked && inputValue.length)
      )

      if (!shouldUpdateCheckbox) return

      handleCheckboxChange()
    },
    [isChecked, handleCheckboxChange]
  )

  return (
    <li className={`checkbox-multi__single-option ${checkedClass}`}>
      <div
        className="checkbox-multi__checkbox"
        onClick={handleCheckboxChange}
        onKeyUp={handleKeyPress}
        tabIndex={0}
        role="checkbox"
        aria-checked={isChecked}
        data-testid={`checkbox-${testId}`}
      >
        <img
          className="checkbox-multi__check-mark"
          src="/assets/check-mark.svg"
          alt="Check mark icon"
        />
      </div>
      <div
        className="checkbox-multi__checkbox-label"
        onClick={handleLabelClick}
      >
        {parse(text)}
      </div>
      {inputField ? (
        <SingleQuestion
          question={{
            ...inputField,
            questionName: {
              name: `${questionGroup}.${inputField.questionName.name}`,
            },
            modifierClass: `checkbox-multi__textarea ${inputField.modifierClass}`,
          }}
          onChange={handleInputChange}
          questionGroup={questionGroup}
          questionGroupValues={questionGroupValues}
        />
      ) : null}
      {subOptions ? (
        <NestedCheckboxMulti
          name={`${questionGroup}.${subOptions.questionName.name}`}
          value={questionGroupValues[subOptions.questionName.name]}
          options={subOptions.options}
          modifierClass={subOptions.modifierClass}
          multiAnswerEnabled={subOptions.multiAnswerEnabled}
          questionGroup={questionGroup}
          questionGroupValues={questionGroupValues}
          question={subOptions} // subOptions is a question object
          parentName={name}
          parentValue={value}
          setField={setField}
        />
      ) : null}
    </li>
  )
}

interface NestedMultiProps {
  name: string
  value: string[]
  options: Option[]
  modifierClass: string
  multiAnswerEnabled: boolean
  questionGroup?: string
  questionGroupValues?: object
  parentName: string
  parentValue: string
  question?: QuestionCheckboxMulti
  setField: (value: object) => void
}

const NestedCheckboxMulti: FC<NestedMultiProps> = ({
  name,
  value,
  options,
  modifierClass,
  multiAnswerEnabled,
  questionGroup,
  questionGroupValues,
  parentName,
  parentValue,
  question,
  setField,
}) => {
  const parentMultiName = useMemo(
    () =>
      questionGroup ? parentName.replace(`${questionGroup}.`, '') : parentName,
    [parentName, questionGroup]
  )

  const handleCheckboxChange = (checkboxValue) => {
    const updatedValues = toggleNestedCheckedValue(
      name,
      value,
      checkboxValue,
      parentName,
      questionGroupValues[parentMultiName],
      parentValue
    )

    setField(updatedValues)
  }

  const shouldShowQuestion = useShouldShowQuestion(
    questionGroupValues,
    question,
    []
  )

  // When the sub options become hidden, clear the sub options
  useEffect(() => {
    // If the sub options are visible, don't clear the sub options
    if (shouldShowQuestion) {
      return
    }

    // Clear the sub options
    setField({ [name]: [] })

    // Clear any input fields that are associated with the sub options
    question?.options?.forEach((option) => {
      if (option?.inputField) {
        option?.inputField?.forEach((inputField) => {
          setField({
            [`${questionGroup}.${inputField.questionName.name}`]: '',
          })
        })
      }
    })
  }, [shouldShowQuestion])

  if (!shouldShowQuestion) {
    return null
  }

  return (
    <div
      className={`checkbox-multi --nested ${modifierClass || ''}`}
      data-testid={`checkbox-multi-${name}`}
    >
      <ul className="checkbox-multi__options">
        {options.map((option, index) => {
          const isChecked = value?.includes(option.value)

          return (
            <Checkbox
              key={`checkbox-multi-${index}`}
              name={name}
              value={option.value}
              isChecked={isChecked}
              text={option.text}
              inputField={option.inputField?.[0]}
              subOptions={option.subOptions?.[0]}
              questionGroup={questionGroup}
              questionGroupValues={questionGroupValues}
              onChange={handleCheckboxChange}
              setField={setField}
              testId={`${parentValue}-${option.value}`}
            />
          )
        })}
      </ul>
    </div>
  )
}

function toggleCheckedValue(
  value: string[],
  checkedItem: string,
  multiAnswerEnabled: boolean
): string[] {
  const checkedValues = new Set(value)

  if (checkedValues.has(checkedItem)) {
    checkedValues.delete(checkedItem)
    return Array.from(checkedValues)
  }

  if (multiAnswerEnabled) {
    checkedValues.add(checkedItem)
    return Array.from(checkedValues)
  } else {
    return [checkedItem]
  }
}

function toggleNestedCheckedValue(
  childKey: string,
  childValue: string[],
  childItem: string,
  parentKey: string,
  parentValue: string[],
  parentItem: string
) {
  const checkedChildValues = new Set(childValue)
  const checkedParentValues = new Set(parentValue)

  if (checkedChildValues.has(childItem)) {
    checkedChildValues.delete(childItem)

    if (!checkedChildValues.size && checkedParentValues.has(parentItem)) {
      checkedParentValues.delete(parentItem)
    }

    return {
      [childKey]: Array.from(checkedChildValues),
      [parentKey]: Array.from(checkedParentValues),
    }
  }

  checkedChildValues.add(childItem)
  checkedParentValues.add(parentItem)

  return {
    [childKey]: Array.from(checkedChildValues),
    [parentKey]: Array.from(checkedParentValues),
  }
}

export default CheckboxMulti
